import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheck,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import "./AboutIntro.css";

const METRICS = [
  { value: 10, suffix: "+", label: "Active clients" },
  { value: 40, suffix: "+", label: "Net Promoter Score" },
  { value: 2, suffix: "", label: "Active social projects" },
];

const CAPABILITIES = [
  "Product strategy",
  "Web and mobile engineering",
  "Responsible technology",
];

const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener?.("change", updatePreference);

    return () => mediaQuery.removeEventListener?.("change", updatePreference);
  }, []);

  return prefersReducedMotion;
};

const AnimatedMetric = ({ value, suffix, label, shouldStart, reduceMotion }) => {
  const [displayValue, setDisplayValue] = useState(reduceMotion ? value : 0);

  useEffect(() => {
    if (!shouldStart) return undefined;

    if (reduceMotion) {
      setDisplayValue(value);
      return undefined;
    }

    let frameId;
    const duration = 1300;
    const startedAt = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      setDisplayValue(Math.round(value * easedProgress));

      if (progress < 1) frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [reduceMotion, shouldStart, value]);

  return (
    <div className="about-intro__metric">
      <strong>
        {displayValue}
        {suffix}
      </strong>
      <span>{label}</span>
    </div>
  );
};

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

function AboutIntro({ id = "about-intro" }) {
  const sectionRef = useRef(null);
  const [metricsVisible, setMetricsVisible] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const section = sectionRef.current;

    if (!section || !("IntersectionObserver" in window)) {
      setMetricsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMetricsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id={id}
      ref={sectionRef}
      className="about-intro"
      aria-labelledby="about-intro-title"
    >
      <div className="about-intro__decoration" aria-hidden="true" />

      <div className="about-intro__container">
        <div className="about-intro__content">
          <p className="about-intro__eyebrow">
            <span aria-hidden="true" />
            About Axiomos
          </p>

          <h1 id="about-intro-title" className="about-intro__title">
            Purpose-led technology for
            <span> meaningful business progress.</span>
          </h1>

          <p className="about-intro__lead">
            Axiomos is a Pune-based custom software partner helping ambitious
            organizations turn complex ideas into dependable digital products.
          </p>

          <p className="about-intro__description">
            We combine product thinking, responsible engineering, and close
            collaboration to build web, mobile, automation, and industry-focused
            solutions. More than ten businesses have trusted us to move from an
            early concept to technology that creates measurable commercial and
            social value.
          </p>

          <ul className="about-intro__capabilities" aria-label="Our approach">
            {CAPABILITIES.map((capability) => (
              <li key={capability}>
                <span aria-hidden="true">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                {capability}
              </li>
            ))}
          </ul>

          <div className="about-intro__actions">
            <ScrollLink to="/contact" className="about-intro__primary-link">
              Start a conversation
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </ScrollLink>

            <ScrollLink to="/projects" className="about-intro__secondary-link">
              Explore our work
            </ScrollLink>
          </div>
        </div>

        <div className="about-intro__visual">
          <div className="about-intro__image-frame">
            <img
              src="/logo.png"
              alt="The Axiomos team collaborating on digital solutions"
              className="about-intro__image"
              loading="eager"
              decoding="async"
            />

            <div className="about-intro__image-shade" aria-hidden="true" />

            <div className="about-intro__image-caption">
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faLocationDot} />
              </span>
              <div>
                <small>Headquartered in</small>
                <strong>Pune, India</strong>
              </div>
            </div>
          </div>

          <blockquote className="about-intro__quote">
            <p>“Lead with purpose. Excellence will follow.”</p>
            <footer>A principle behind every partnership</footer>
          </blockquote>
        </div>
      </div>

      <div className="about-intro__metrics" aria-label="Axiomos in numbers">
        <div className="about-intro__metrics-inner">
          <p className="about-intro__metrics-label">Our growing impact</p>

          <div className="about-intro__metrics-grid">
            {METRICS.map((metric) => (
              <AnimatedMetric
                key={metric.label}
                {...metric}
                shouldStart={metricsVisible}
                reduceMotion={reduceMotion}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default AboutIntro;
